import * as React from 'react';
import { Loader, LoaderSize, LoaderTheme } from '../Loader/Loader';
import { useCallback, useEffect, useRef, useState } from 'react';
import { instance } from '@viz-js/viz';
import * as  svgPanZoom from 'svg-pan-zoom';
import * as d3 from 'd3';
import * as _ from 'lodash';
import cancelEvent from '../../../services/mini/cancelEvent';
import { Environment } from '../../../const/environment';
import Overlay from '../Overlay/Overlay';
import GraphRendererDetailsPopup from './GraphRendererDetailsPopup/GraphRendererDetailsPopup';
import { convertTransformPropValueToString, formatValueForTransform } from '../../pages/project/TransformMode/formatValueForTransform';
import store from '../../../store';
import ToasterService from '../../../services/ToasterService';

function parseFileName(text) {
    const regex = /'([^']+)'/;
    const match = text.match(regex);

    if (match && match[1]) {
        return match[1];
    } else {
        return null;
    }
}


function getSvg(graph, engine, svg) {
    return new Promise<SVGElement>((resolve, reject) => {
        if (svg) {
            resolve(svg);
        } else {

            try {
                instance().then(viz => {
                    resolve(viz.renderSVGElement(graph, { engine }));
                });
            } catch (error) {
                reject(error);
            }
        }
    });
}

const dragThreshold = 5;

type GraphRendererProps = {
    graph: string | null | undefined;
    engine: string;
    projectId: string;
    environment: Environment
    actionsMap: object
    onClose: () => void;
    svg?: SVGElement | null | undefined;
    onPanZoomChange?: (zoomData, panData) => void;
    predefinedZoom?: object
    predefinedPan?: object
    error?: string
    enabledNodes?: string[]
}

export function GraphRenderer({
    graph,
    engine,
    environment,
    actionsMap,
    projectId,
    onClose,
    onPanZoomChange,
    svg,
    predefinedZoom,
    predefinedPan,
    error,
    enabledNodes = []
}: GraphRendererProps) {
    const [isRenderInProcess, setIsRenderInProcess] = useState(false);
    const [renderError, setRenderError] = useState<string | null>(null);
    const [detailId, setDetailId] = useState('');
    const [detailTitle, setDetailTitle] = useState('');

    const graphRef = useRef<HTMLDivElement>();
    const panZoom = useRef<any>({});// todo: get rid of any
    const actionsMapRef = useRef({});
    const isDraggingRef = useRef(false);
    const xRef = useRef<number>();
    const yRef = useRef<number>();

    useEffect(() => {
        actionsMapRef.current = actionsMap;
    });

    const resetSelection = useCallback(() => {
        d3.selectAll('#graph .selected').classed('selected', false);
    }, []);


    useEffect(() => {
        document.addEventListener('click', resetSelection);
        return () => {
            document.removeEventListener('click', resetSelection);
        };
    }, [resetSelection]);

    useEffect(() => {
        highlightSearchResults();
    }, [enabledNodes]);

    const highlightSearchResults = useCallback(() => {
        if (enabledNodes.length > 0) {
            d3
                .selectAll('.node,.edge')
                .classed("disabled-node-for-search", false)
                .filter(function () {
                    const titleText = d3.select(this).select("title").text();
                    const isNode = d3.select(this).classed('node');
                    const isEdge = d3.select(this).classed('edge');

                    if (isNode) {
                        return !enabledNodes.some(text => titleText?.toLowerCase() === text?.toLowerCase());
                    }

                    if (isEdge) {
                        const [firstPart, secondPart] = titleText.split('->').map(part => part?.trim());

                        if (enabledNodes.includes(firstPart) && enabledNodes.includes(secondPart)) {
                            return false;
                        } else {
                            return true;
                        }
                    }

                    return false;
                })
                .classed("disabled-node-for-search", true);
        } else {
            d3
                .selectAll('.node,.edge')
                .classed("disabled-node-for-search", false);
        }
    }, [enabledNodes]);


    const addGraphEvents = useCallback((pass) => {
        const nodes = d3.selectAll('.node,.edge,.w-clicked-elements text');
        nodes.on("click", function (e) {
            if (isDraggingRef.current) {
                cancelEvent(e);
                return;
            }
            const closestAWithSpecialHref = e.target.closest('a');
            if (closestAWithSpecialHref && (closestAWithSpecialHref.getAttribute('href')?.indexOf('javascript:') > -1
                || closestAWithSpecialHref.getAttribute('xlink:href')?.indexOf('javascript:') > -1)) {

                const project = store.getState().projectsInfo.currentProject;
                const transformName = parseFileName(closestAWithSpecialHref.getAttribute('xlink:href'));

                if (_.find(project.scripts, { name: transformName })) {
                    onClose();
                } else {
                    ToasterService.showWarning({ title: `Transform <b>${transformName}</b> not found in the current project.` })
                }

                return;
            }

            cancelEvent(e);
            resetSelection();
            const el = this.closest('[id]');

            if (el?.classList?.contains('node')) {
                const id = el.getAttribute('id');
                console.info('Id:', id);
                if (actionsMapRef?.current?.[id]) {
                    setDetailId(id);
                    setDetailTitle(el?.querySelector('title')?.innerHTML);
                }
                el.classList.add("selected");
            } else if (el?.firstChild?.tagName?.toLowerCase() === 'a') {
                const id = el.getAttribute('id')?.replace('a_', '');
                console.info('Id:', id);
                if (actionsMapRef?.current?.[id]) {
                    setDetailId(id);
                    setDetailTitle(el?.querySelector('a')?.getAttribute('href'));
                }
                el.classList.add("selected");
            } else {
                this.classList.add("selected");
            }
        });
    }, [resetSelection, actionsMapRef, onClose]);

    const onPanZoomChangeDebounced = _.debounce((zoom, pan) => {
        onPanZoomChange(zoom, pan);
    }, 500);

    useEffect(() => {
        if (!graph && !svg) return;

        const renderSvg = async () => {
            setIsRenderInProcess(true);

            console.time('render graph');

            console.info('render graph with engine: ', engine);

            const height = graphRef?.current?.clientHeight || 500;
            const width = graphRef?.current?.clientWidth || 900;

            try {
                const svgEl = await getSvg(graph, engine, svg);
                svgEl.setAttribute('width', width + 'px');
                svgEl.setAttribute('height', height + 'px');

                setIsRenderInProcess(false);
                document.getElementById("graph").innerHTML = '';
                document.getElementById("graph").appendChild(svgEl);

                const svgD3 = d3.select('#graph svg');

                svgD3.on("mousedown", function (event) {
                    xRef.current = event.clientX;
                    yRef.current = event.clientY;
                    isDraggingRef.current = false;
                });

                svgD3.on("mousemove", function (event) {
                    if (event.buttons === 1) {
                        const dx = event.clientX - xRef.current;
                        const dy = event.clientY - yRef.current;
                        if (Math.sqrt(dx * dx + dy * dy) > dragThreshold) {
                            isDraggingRef.current = true;
                        }
                    }
                });

                panZoom.current = svgPanZoom(svgEl, {
                    zoomEnabled: true,
                    controlIconsEnabled: true,
                    fit: true,
                    center: true,
                    minZoom: 0.1,
                    maxZoom: 400
                });

                if (predefinedZoom) {
                    panZoom.current.zoom(predefinedZoom);
                }

                if (predefinedPan) {
                    panZoom.current.pan(predefinedPan);
                }

                if (onPanZoomChange) {
                    panZoom.current.setOnZoom((zoomLevel) => {
                        onPanZoomChangeDebounced(zoomLevel, panZoom.current.getPan());
                    });

                    panZoom.current.setOnPan((pan) => {
                        onPanZoomChangeDebounced(panZoom.current.getZoom(), pan);
                    });
                }

                svgEl.addEventListener('paneresize', function (e) {
                    panZoom.current.resize();
                }, false);
                window.addEventListener('resize', function (e) {
                    panZoom.current.resize();
                });
                console.timeEnd('render graph');


                setIsRenderInProcess(false);
                setRenderError(null);

                addGraphEvents(actionsMap);
            } catch (error) {
                setIsRenderInProcess(false);
                setRenderError(error);
            }
        };
        renderSvg();
    }, [engine, graph, svg, onPanZoomChange]);

    return <div className="graph-renderer">
        {graph === undefined ?
            <Loader theme={LoaderTheme.Dark} size={LoaderSize.Medium} position="absolute" center={true} /> :
            <React.Fragment>
                {graph === null ?
                    <div style={{ textAlign: 'center', marginTop: '40px' }}>{error || 'Unable to retrieve data. Please try again later'}</div> :
                    <div className={`graph-renderer__graph ${isRenderInProcess ? 'disabled' : ''} ${renderError ? 'error' : ''}`} id="graph" ref={graphRef} />
                }
            </React.Fragment>}
        {detailId &&
            <Overlay>
                <GraphRendererDetailsPopup
                    projectId={projectId}
                    title={actionsMapRef?.current?.[detailId]?.name || detailTitle}
                    format={actionsMapRef?.current?.[detailId]?.type}
                    initValue={convertTransformPropValueToString(actionsMapRef?.current?.[detailId]?.value)}
                    onClose={() => {
                        setDetailId(null);
                        setDetailTitle(null);
                    }}
                    environment={environment}

                />
            </Overlay>}
        {isRenderInProcess && <Loader theme={LoaderTheme.Dark} size={LoaderSize.Medium} position="absolute" center={true} />}
        {renderError && <div className="graph-renderer__graph-render-err-banner">{`Graph rendering failed with the error: ${renderError}.`}<br /> </div>}
    </div>;
}